import { Drawer } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import AddJobsByMap from './AddJobsByMap/AddJobsByMap'
import AddJobsWorkareas from './AddJobsWorkareas'
import * as routeAddModel from './routeAddModel'
import { RouteJob } from '../routingApi'
import { useJobsForCreateRouteQuery } from '../routingQueries'
import { TenantLocation } from '../../../domain/tenantLocations/tenantLocation'

export type Props = {
  isOpen: boolean
  onClose: () => void
  onSaveSuccess?: (jobs: RouteJob[]) => void
  existingJobs?: RouteJob[]
  routeId?: number
  editJobId?: number | null
  initialMapViewState: any
  shopLocation: TenantLocation | null
}

const AddJobs = ({ isOpen, onClose, onSaveSuccess, existingJobs, routeId, editJobId, initialMapViewState, shopLocation } : Props) => {
  const [currentStep, setCurrentStep] = useState<'map' | 'workareas'>('map')
  const [selectedJobs, setSelectedJobs] = useState<number[]>([])
  const [selectedJobsData, setSelectedJobsData] = useState<routeAddModel.Job[]>([])
  const [startingLocation, setStartingLocation] = useState<RouteJob | null>(null)
  const [endingLocation, setEndingLocation] = useState<RouteJob | null>(null)

  const { data: pickerJobs } = useJobsForCreateRouteQuery();

  const jobs = useMemo(
    () => routeAddModel.withRouteJobsIncluded(pickerJobs, existingJobs),
    [pickerJobs, existingJobs]
  )

  const routeJobIds = useMemo(
    () => routeAddModel.getRouteJobIds(existingJobs),
    [existingJobs]
  )

  const handleNext = (jobs: number[], jobsData: routeAddModel.Job[]) => {
    setSelectedJobs(jobs)
    setSelectedJobsData(jobsData)
    setCurrentStep('workareas')
  }

  const handleBack = () => {
    setCurrentStep('map')
  }

  const handleClose = () => {
    onClose()
    setCurrentStep('map')
  }

  const handleSave = (state: RouteJob[]) => {
    const finalJobs = routeAddModel.buildFinalRouteJobs(
      state,
      routeId!,
      startingLocation,
      endingLocation,
      shopLocation
    )

    onSaveSuccess?.(finalJobs)
    handleClose()
  }

  useEffect(() => {
    if (existingJobs) {
      setSelectedJobs(existingJobs.filter(job => job.jobId != null).map(job => job.jobId!))
      const sortedJobs = existingJobs.slice().sort((a, b) => a.sortIndex - b.sortIndex)
      
      const firstJob = sortedJobs[0]
      if (firstJob) {
        setStartingLocation(firstJob)
      }
      
      const lastJob = sortedJobs[sortedJobs.length - 1]
      if (lastJob) {
        setEndingLocation(lastJob)
      }
    }
  }, [existingJobs])

  useEffect(() => {
    if (editJobId && existingJobs) {
      setCurrentStep('workareas')
      setSelectedJobsData(jobs?.filter(job => existingJobs.find(existingJob => existingJob.jobId === job.jobId)) || [])
    }
  }, [editJobId, existingJobs])

  return <Drawer
    placement="bottom"
    onClose={handleClose}
    open={isOpen}
    height="97vh"
    closable={false}
    maskClosable={false}
    styles={{
      body: {
        padding: 0,
        position: 'relative',
      },
      mask: {
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
      },
      content: {
        borderTopLeftRadius: '16px',
        borderTopRightRadius: '16px',
      }
    }}
  >
    {currentStep === 'map' ? (
      <AddJobsByMap
        isOpen={isOpen}
        onClose={handleClose} 
        onNext={handleNext} 
        jobs={jobs}
        routeJobIds={routeJobIds}
        initialSelectedJobs={selectedJobs} 
        initialMapViewState={initialMapViewState}
      />
    ) : (
      <AddJobsWorkareas 
        isOpen={isOpen}
        selectedJobs={selectedJobsData}
        onBack={handleBack} 
        onClose={handleClose} 
        onSelectedJobsChange={(jobs) => {
          setSelectedJobsData(jobs)
          setSelectedJobs(jobs.map(job => job.jobId))
        }}
        existingJobs={existingJobs}
        onSave={handleSave}
        editJobId={editJobId}
      />
    )}
  </Drawer>
}

export default AddJobs